/* eslint-disable class-methods-use-this */
/**
 * V2 text cell template — adds skeleton state while block outputs load.
 * Extends TextCellTemplateOpz so legacy tables stay unchanged.
 */
import React from 'react';
import { Compatible } from '@silevis/reactgrid';
import { Flex, Skeleton } from '@chakra-ui/react';

import {
  TextCell,
  TextCellTemplateOpz,
} from 'pages/PlanPageDetailsOPZ/Inputs/IndicatorPageMultiDim/TextCellTemplateOpz';
import { FormulaErrorBox, IndicatorIcon } from 'utils/ReactGridUtils';
import formatValue from 'utils/FormatValues';
import { CURRENCY_OPTIONS } from 'projectConstants';

export interface TextCellV2 extends TextCell {
  isLoading?: boolean;
  formatProperties?: Record<string, any> | null;
}

/**
 * `measure` arrives as a currency code ("INR"), but the grid shows symbols.
 * Anything that isn't a known code — "%" or an already-resolved "₹" — is left
 * alone, so this never turns a valid symbol into the wrong currency.
 */
const toDisplayMeasure = (measure?: string) =>
  CURRENCY_OPTIONS.find((option) => option.value === measure)?.symbol ?? measure;

export class TextCellTemplateOpzV2 extends TextCellTemplateOpz {
  render(
    cell: Compatible<TextCellV2>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<TextCellV2>, commit: boolean) => void,
  ): React.ReactNode {
    if (!isInEditMode && cell?.isLoading) {
      return (
        <Flex width='100%' height='100%' alignItems='center' justifyContent='flex-end' px={2}>
          <Skeleton
            startColor='rgba(255, 255, 255, 0.12)'
            endColor='rgba(255, 255, 255, 0.35)'
            height='14px'
            width='72%'
            borderRadius='4px'
          />
        </Flex>
      );
    }

    if (!isInEditMode) {
      const { indicatorType, text, hasFormulaError } = cell || {};
      const errorMessage = cell?.hasFormulaErrorMessage?.map((item: any) => item);
      const parsed = cell?.format ? Number(text) : cell.text;
      // A non-numeric/absent value renders as 0 rather than "NaN" — an
      // indicator with no computed output for a period reads as zero.
      const cellText: any = typeof parsed === 'number' && Number.isNaN(parsed) ? 0 : parsed;
      const format = cell.format?.toLocaleLowerCase();
      const cellFilteredText = cell?.formatProperties
        ? // The indicator's own format properties (measure, decimals, scale,
          // separators) win — formatValue produces the complete display string.
          formatValue(cellText, {
            ...cell.formatProperties,
            measure: toDisplayMeasure(cell.formatProperties.measure),
          } as any)
        : format === 'number'
        ? `${this.numberWithCommas(cellText?.toFixed(2))}`
        : format === 'integer'
        ? `${this.numberWithCommas(cellText?.toFixed(0))}`
        : format === 'percentage'
        ? `${cellText?.toFixed(1)}%`
        : format === 'currency'
        ? cellText < 0
          ? `(${cell?.currencyType}${this.numberWithCommas(Math.abs(cellText?.toFixed(2)))})`
          : `${cell?.currencyType}${this.numberWithCommas(cellText?.toFixed(2))}`
        : cellText;

      return (
        <div
          style={{
            width: '100%',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            fontSize: '15px',
            display: 'flex',
            justifyContent:
              cell?.colType === 'header'
                ? 'space-between'
                : cell?.tableName === 'block'
                ? 'end'
                : 'flex-start',
            alignItems: 'center',
          }}
        >
          <Flex width={'100%'} alignItems={'center'} justifyContent={'space-between'}>
            <Flex width={'100%'}>
              <IndicatorIcon indicatorType={indicatorType} />
              <p style={{ width: '100%', textAlign: format ? 'right' : 'start' }}>
                {`${cellFilteredText}` || cell?.placeholder || ''}
              </p>
            </Flex>
            <FormulaErrorBox hasFormulaError={hasFormulaError} errorMessage={errorMessage} />
          </Flex>
        </div>
      );
    }

    return super.render(cell, isInEditMode, onCellChanged);
  }
}
